import * as React from 'react';
import classnames from 'classnames';
import { FormFeedback } from 'reactstrap';
import { isEmptyObject } from 'utils/validation';
import { formInputTypes } from 'commons/constants';
import ButtonOptions from 'components/uielements/ButtonOptions';
import DateInput from 'components/uielements/DateInput';
import TextareaInput from 'components/uielements/TextareaInput';
import DefaultInput from './components/DefaultInput';
import SelectInput from './components/SelectInput';
import FormInputWrapper from './FormInput.style';
import type {
  FormInputProps,
  ErrorFeedbackProps,
  ErrorMessageProps,
  InputComponentProps,
  LabelProps,
  RenderInputParams,
  TextCounterProps,
} from './FormInput.types';

const { BOOLEAN, DATE, SELECT, TEXTAREA } = formInputTypes;

const inputClassnames = (isTouched: boolean, isValid: boolean): string => {
  const classnamesSettings = {
    invalid: isTouched && !isValid,
    valid: isTouched && isValid,
  };
  return classnames(classnamesSettings);
};

const getFeedbackClass = (feedback: boolean | null | undefined): string =>
  feedback ? '' : '--no-feedback';

const getHasDataClass = (value: any): string => (value ? 'has-data' : '');

const getIsInvalidClass = (isValid: boolean): string => (isValid ? '' : 'is-invalid');

const getNoodleInputClass = (
  inputClass: string,
  feedbackClass: string,
  hasDataClass: string,
  isInvalidClass: string,
): string => {
  const inputNumberClass = inputClass === 'number' ? 'is-number' : '';
  return `form-input__input${feedbackClass} ${hasDataClass} ${isInvalidClass} ${inputNumberClass}`;
};

const getInputPropsObject = (props: RenderInputParams): InputComponentProps => {
  const {
    disabled,
    errors,
    feedback,
    inputClassName,
    inputType = 'text',
    isSaving,
    max,
    maxLength,
    name,
    masks,
    min,
    onChange,
    onKeyPress,
    onSuggestionsCleared,
    onSuggestionsFetched,
    onSuggestionsUpdated,
    onSuggestionSelected,
    options,
    placeholder,
    rows,
    step,
    suggestions,
    value,
  } = props;
  const isValid = isEmptyObject(errors);
  const feedbackClass = getFeedbackClass(feedback);
  const hasDataClass = getHasDataClass(value);
  const isInvalidClass = getIsInvalidClass(isValid);
  const noodleInputClass = getNoodleInputClass(
    inputType,
    feedbackClass,
    hasDataClass,
    isInvalidClass,
  );
  return {
    ...props,
    className: inputClassName,
    disabled,
    inputClassName,
    inputType,
    isInvalidClass,
    isSaving,
    isValid,
    feedbackClass,
    hasDataClass,
    masks,
    max,
    maxLength,
    min,
    name,
    noodleInputClass,
    onChange,
    onKeyPress,
    onSuggestionsUpdated,
    onSuggestionsCleared,
    onSuggestionsFetched,
    onSuggestionSelected,
    options,
    placeholder,
    rows,
    step,
    suggestions,
    value,
  };
};

const ErrorMessage = ({ errorMsg }: ErrorMessageProps): React.ReactElement<any> | null =>
  errorMsg && errorMsg !== '' ? (
    <span className='form-input__label error text-timestamp'> - {errorMsg}</span>
  ) : null;

const ErrorFeedback = ({
  errorDetails,
  isValid,
}: ErrorFeedbackProps): React.ReactElement<any> | null =>
  errorDetails.length > 0 ? (
    <FormFeedback
      className={`text-timestamp ${classnames({
        invalid: !isValid,
      })}`}
    >
      <ul>
        {errorDetails.map((err) => (
          <li key={err.id}>{err.text}</li>
        ))}
      </ul>
    </FormFeedback>
  ) : null;

const renderInput = (props: RenderInputParams): React.ReactElement<any> | null => {
  const { type } = props;
  const inputProps = getInputPropsObject(props);

  switch (type) {
    case BOOLEAN:
      return <ButtonOptions {...props} />;

    case DATE:
      return <DateInput {...inputProps} />;

    case SELECT:
      return <SelectInput {...inputProps} />;

    case TEXTAREA:
      return <TextareaInput {...props} />;

    default:
      return <DefaultInput {...inputProps} />;
  }
};

const TextCounter = ({
  textCounter,
  value = '',
  maxLength = 0,
  showCounter,
}: TextCounterProps): React.ReactElement<any> | null => {
  if (typeof value === 'string' && textCounter && showCounter) {
    const valueLength = value.length;
    const errorClass = valueLength === maxLength ? 'form-input__label error' : '';
    const minimumIntegerDigits = maxLength.toString().length;
    const valueString = valueLength.toLocaleString('en-US', {
      minimumIntegerDigits,
    });
    return (
      <span className={errorClass}>
        {valueString}/
        {maxLength.toLocaleString('en-US', {
          minimumIntegerDigits,
        })}
      </span>
    );
  }

  return null;
};

const Label = ({
  counterProps,
  errorMsg,
  label,
  textCounterClass,
}: LabelProps): React.ReactElement<any> | null =>
  label ? (
    <div className={`form-input__label-container ${textCounterClass}`}>
      <span className='form-input__label text-content-body semi-bold'>{label}</span>
      <TextCounter {...counterProps} />
      <ErrorMessage errorMsg={errorMsg} />
    </div>
  ) : null;

const FormInput = (props: FormInputProps) => {
  const {
    className = '',
    containerClass,
    errors = {},
    isTouched = true,
    isSaving,
    label,
    maxLength,
    textCounter,
    value,
  } = props;
  const { errorDetails = [], errorMsg = '' } = errors;
  const isValid = isEmptyObject(errors);
  const inputClassName = `${className} ${inputClassnames(isTouched, isValid)}`;
  const showCounter = errorMsg === '';
  const textCounterClass = showCounter && textCounter ? 'text-counter' : '';
  const disabled = isSaving !== undefined && isSaving;
  const counterProps = {
    textCounter,
    value,
    maxLength,
    showCounter,
  };
  const labelProps = {
    counterProps,
    errorMsg,
    label,
    textCounterClass,
  };
  return (
    <FormInputWrapper className={containerClass}>
      <Label {...labelProps} />
      {renderInput({ ...props, inputClassName, disabled })}
      <ErrorFeedback errorDetails={errorDetails} isValid={isValid} />
    </FormInputWrapper>
  );
};

export default FormInput;
